import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Subscriptions as SubscriptionsIcon,
  AttachMoney as MoneyIcon,
  Verified as VerifiedIcon,
  Photo as PhotoIcon,
  Report as ReportIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { dashboardAPI } from '../../services/api';
import StatsCards from './StatsCards';
import ChartWidgets from './ChartWidgets';
import RecentActivities from './RecentActivities';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [activities, setActivities] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, chartsRes, activitiesRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getCharts(),
        dashboardAPI.getActivities()
      ]);
      
      setStats(statsRes.data.data);
      setChartData(chartsRes.data.data);
      setActivities(activitiesRes.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
      // Set empty data to prevent errors
      setStats({
        totalUsers: 0,
        newUsers: 0,
        activeSubscriptions: 0,
        totalRevenue: 0,
        pendingVerifications: 0,
        pendingPhotos: 0,
        pendingReports: 0
      });
      setChartData({
        registrations: [],
        revenueTrends: [],
        topCities: [],
        topReligions: []
      });
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    { title: 'Total Users', value: stats?.totalUsers || 0, icon: <PeopleIcon />, color: '#1976d2' },
    { title: 'New Users (7d)', value: stats?.newUsers || 0, icon: <PersonAddIcon />, color: '#2e7d32' },
    { title: 'Active Subscriptions', value: stats?.activeSubscriptions || 0, icon: <SubscriptionsIcon />, color: '#ed6c02' },
    { title: 'Total Revenue', value: `₨ ${stats?.totalRevenue?.toLocaleString() || 0}`, icon: <MoneyIcon />, color: '#9c27b0' },
    { title: 'Pending Documents', value: stats?.pendingVerifications || 0, icon: <VerifiedIcon />, color: '#d32f2f' },
    { title: 'Pending Photos', value: stats?.pendingPhotos || 0, icon: <PhotoIcon />, color: '#f57c00' },
    { title: 'User Reports', value: stats?.pendingReports || 0, icon: <ReportIcon />, color: '#e91e63' }
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Dashboard
        </Typography>
        <IconButton onClick={fetchData} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <StatsCards stats={statCards} />

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} lg={8}>
          <ChartWidgets data={chartData} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <RecentActivities activities={activities} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;